import { useState } from 'react'
import { motion } from 'framer-motion'
import Card from './ui/Card'
import Button from './ui/Button'
import Badge from './ui/Badge'

const MotionCard = motion.create(Card)

const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }

const STATUS_LABELS = {
    published: 'Published',
    preprint: 'Preprint',
    under_review: 'Under Review',
    in_progress: 'In Progress'
}

const STATUS_TONES = {
    published: 'success',
    preprint: 'accent',
    under_review: 'warning',
    in_progress: 'default',
}

export default function PublicationCard({ title, authors = [], venue, year, status, abstract, paperUrl, arxivUrl, codeUrl, bibtex, featured }) {
    const [copied, setCopied] = useState(false)

    const handleCopyBibtex = async () => {
        try {
            await navigator.clipboard.writeText(bibtex)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch {
            // clipboard unavailable; nothing further to do
        }
    }

    return (
        <MotionCard
            variants={item}
            whileHover={{ y: -2 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20, mass: 0.6 }}
            accent={featured}
            className="group flex w-full flex-col p-5 shadow-brutal hover:shadow-brutal-hover"
        >
            <div className="flex items-start justify-between gap-3">
                <h2 className="font-serif text-lg font-bold transition-colors text-black dark:text-white group-hover:text-accent">{title}</h2>
                {status && <Badge tone={STATUS_TONES[status] || 'default'}>{STATUS_LABELS[status] || status}</Badge>}
            </div>

            {authors.length > 0 && (
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{authors.join(', ')}</p>
            )}
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-500">
                {venue}{venue && year ? ' · ' : ''}{year}
            </p>

            {abstract && (
                <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">{abstract}</p>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
                {paperUrl && (
                    <Button as="a" href={paperUrl} target="_blank" rel="noopener noreferrer" variant="primary">
                        Paper ↗
                    </Button>
                )}
                {arxivUrl && (
                    <Button as="a" href={arxivUrl} target="_blank" rel="noopener noreferrer" variant="secondary">
                        arXiv ↗
                    </Button>
                )}
                {codeUrl && (
                    <Button as="a" href={codeUrl} target="_blank" rel="noopener noreferrer" variant="secondary">
                        Code ↗
                    </Button>
                )}
                {bibtex && (
                    <Button variant="ghost" onClick={handleCopyBibtex}>
                        {copied ? 'Copied' : 'Copy BibTeX'}
                    </Button>
                )}
            </div>
        </MotionCard>
    )
}
